// src/components/teachers/FeeFormComponent.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom'; // Added useLocation
import {
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    DialogContentText,
    Snackbar,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Grid,
    CircularProgress,
    InputAdornment, // For icons in text fields
} from '@mui/material';
import axiosInstance from '../../api/axiosConfig';
import { getApiErrorMessage } from '../../api/errorUtils';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountCircleIcon from '@mui/icons-material/AccountCircle'; // For student details
import PhoneIcon from '@mui/icons-material/Phone';
import SchoolIcon from '@mui/icons-material/School'; // For Date
import BadgeIcon from '@mui/icons-material/Badge'; // For Admission No
import TodayIcon from '@mui/icons-material/Today'; // For Date
import PaymentsIcon from '@mui/icons-material/Payments'; // For Payment Mode
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; // For Reference
import CommentIcon from '@mui/icons-material/Comment'; // For Comments
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'; // For Amount
import { styled } from '@mui/system';

// --- Styled Components ---
const PageContainer = styled(Box)(({ theme }) => ({ // Added container for consistent padding
    padding: theme.spacing(3),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3, 4), // Adjusted padding
    margin: '0 auto', // Removed top/bottom margin, handled by PageContainer
    maxWidth: 800, // Slightly adjusted max width
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 1.5, // More rounded
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1), // Space between icon and text
}));

const StudentDetailsPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.grey[50], // Lighter background for distinction
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const DetailItem = styled(Box)(({ theme }) => ({ // Component for each student detail item
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5), // Space between icon and text
    marginBottom: theme.spacing(1),
}));

const DetailText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    '& strong': {
        color: theme.palette.text.primary,
        fontWeight: 500,
    },
}));

const ConfirmationItem = styled(Typography)(({ theme }) => ({ // For confirmation dialog items
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    borderLeft: `3px solid ${theme.palette.primary.light}`,
}));

const CurrencyDisplay = styled(Typography)(({ theme }) => ({ // For displaying formatted amount inline
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
    marginLeft: theme.spacing(1),
}));

// --- Utility Functions ---

// Format date to YYYY-MM-DDTHH:mm (suitable for datetime-local input if needed, or backend)
// Keeping your backend format function if it's required
const formatDateTimeForBackend = (date = new Date()) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    // Adjust format based on backend expectation (e.g., with 'T' separator)
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};


// Format date as DD/MM/YYYY for display
const formatDateForDisplay = (date) => {
    if (!date) return 'N/A';
    try {
        const d = new Date(date);
        // Check if date is valid after parsing
        if (isNaN(d.getTime())) return 'Invalid Date';
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (e) {
        console.error("Error formatting date:", date, e);
        return 'Invalid Date';
    }
};

// Format amount with commas and optional decimal places
const formatAmountForDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '0.00';
    const num = Number(value);
    if (isNaN(num)) return '0.00';
    // Format with commas and exactly two decimal places
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};


// --- FeeFormComponent ---

const FeeFormComponent = () => {
    const { id: feeIdParam } = useParams(); // Rename to avoid conflict
    const location = useLocation(); // Use location to get query params reliably
    const navigate = useNavigate();
    const schoolId = useMemo(() => localStorage.getItem('schoolId'), []); // Memoize

    // Initialize state
    const [fee, setFee] = useState({
        amount: '',
        paymentDate: formatDateTimeForBackend(), // Default to now for backend
        paymentMode: 'Cash',
        status: 'Paid', // Default status
        schoolId: schoolId || '',
        studentId: '',
        referenceNumber: '',
        comments: '',
    });
    const [student, setStudent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false); // For confirm button loading
    const [openDialog, setOpenDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formErrors, setFormErrors] = useState({}); // Use object for multiple errors

    // Get studentId from query parameters
    const studentId = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('studentId');
    }, [location.search]);

    // Fetch Student Details Effect
    useEffect(() => {
        const fetchStudentDetails = async () => {
            if (!studentId) {
                setSnackbar({ message: 'Student ID is missing in the URL.', severity: 'error', open: true });
                setLoading(false);
                // Consider navigating back or showing a persistent error state
                // navigate(-1); // Example: navigate back
                return;
            }
             if (!schoolId) {
                setSnackbar({ message: 'School ID is missing. Please log in again.', severity: 'error', open: true });
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/students/${studentId}`, {
                     headers: { schoolId }
                });
                const studentData = response.data;
                setStudent(studentData);
                // Pre-fill fee state only after student data is successfully fetched
                setFee(prev => ({
                    ...prev,
                    schoolId: studentData.schoolId || schoolId, // Use fetched or local schoolId
                    studentId: studentData.studentId,
                }));
            } catch (error) {
                console.error('Error fetching student details:', error);
                setSnackbar({ message: getApiErrorMessage(error, 'Error fetching student details.'), severity: 'error', open: true });
                 // Handle specific errors like 404 Not Found
                 if (error.response && error.response.status === 404) {
                     // Maybe navigate back or show "Student Not Found" message
                 }
            } finally {
                setLoading(false);
            }
        };

        fetchStudentDetails();
    }, [studentId, schoolId]); // Dependencies

    // Fetch Fee Details if Editing (feeIdParam exists) - TO BE IMPLEMENTED if needed
    // useEffect(() => {
    //     if (feeIdParam) {
    //         // Fetch existing fee details logic here...
    //         // setFee(fetchedFeeData);
    //     }
    // }, [feeIdParam]);


    // Input Change Handler
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFee(prevState => ({ ...prevState, [name]: value }));

        // Clear specific error when user types
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    // Form Validation
    const validateForm = () => {
        const errors = {};
        const amountValue = parseFloat(fee.amount);
        if (!fee.amount || isNaN(amountValue) || amountValue <= 0) {
            errors.amount = 'Amount must be a positive number.';
        }
        // Add other validation rules if needed (e.g., reference number format for certain modes)
        if (fee.paymentMode === 'Cheque' && !fee.referenceNumber?.trim()) {
             errors.referenceNumber = 'Cheque number is required for Cheque payments.';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle Form Submit -> Open Confirmation Dialog
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setOpenDialog(true);
        }
    };

    // Confirm Submission -> API Call
    const confirmSubmit = async () => {
        if (!studentId) {
             setSnackbar({ message: 'Cannot submit: Student ID is missing.', severity: 'error', open: true });
             setOpenDialog(false);
             return;
        }

        setIsSubmitting(true);
        setOpenDialog(false); // Close dialog immediately

        // Prepare data - ensure amount is number, date is formatted correctly
        const payload = {
            ...fee,
            amount: parseFloat(fee.amount),
            paymentDate: formatDateTimeForBackend(fee.paymentDate), // Ensure backend format
            studentId: parseInt(studentId, 10), // Ensure studentId is number
            schoolId: parseInt(schoolId, 10), // Ensure schoolId is number
        };

        const apiCall = feeIdParam
            ? axiosInstance.put(`/api/fees/${feeIdParam}`, payload) // Update if feeIdParam exists
            : axiosInstance.post('/api/fees', payload); // Create otherwise

        try {
            await apiCall;
            setSnackbar({ message: `Fee entry successfully ${feeIdParam ? 'updated' : 'created'}!`, severity: 'success', open: true });
            // Redirect after a short delay
            setTimeout(() => {
                // Navigate back to student fee management page or list
                navigate(`/students/${studentId}/assign-fee`); // Or appropriate route
            }, 1500);
        } catch (error) {
            console.error('Error submitting fee:', error);
            setSnackbar({ message: getApiErrorMessage(error, 'Error submitting fee. Please try again.'), severity: 'error', open: true });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Snackbar Close Handler
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };


    // --- Render Logic ---

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading Student Details...</Typography>
            </Box>
        );
    }

    // If loading is done but student is still null (and no feeIdParam for edit mode), show error
     if (!student && !feeIdParam) {
         return (
             <PageContainer>
                 <StyledPaper>
                     <Alert severity="error">
                         Could not load student details. Please check the Student ID or go back and try again.
                     </Alert>
                 </StyledPaper>
             </PageContainer>
         );
     }


    return (
        <PageContainer>
            <StyledPaper elevation={3}>
                <Typography variant="h5" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
                    {feeIdParam ? 'Edit Fee Entry' : 'Record New Fee Payment'}
                </Typography>

                {/* Student Details Section */}
                {student && (
                    <StudentDetailsPaper variant="outlined">
                        <SectionTitle variant="h6">
                            <AccountCircleIcon color="action" /> Student Information
                        </SectionTitle>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                                <DetailItem>
                                    <BadgeIcon fontSize="small" color="action" />
                                    <DetailText><strong>Name:</strong> {`${student.firstName || ''} ${student.lastName || ''}`}</DetailText>
                                </DetailItem>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailItem>
                                    <BadgeIcon fontSize="small" color="action" />
                                    <DetailText><strong>Admission No:</strong> {student.admissionNumber || 'N/A'}</DetailText>
                                </DetailItem>
                            </Grid>
                             <Grid item xs={12} sm={6}>
                                <DetailItem>
                                    <SchoolIcon fontSize="small" color="action" /> {/* Replace with actual Class icon if available */}
                                    <DetailText><strong>Class:</strong> {student.className || 'N/A'}</DetailText> {/* Assuming className exists */}
                                </DetailItem>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailItem>
                                    <PhoneIcon fontSize="small" color="action" />
                                    <DetailText><strong>Phone:</strong> {student.phone || 'N/A'}</DetailText>
                                </DetailItem>
                            </Grid>
                            {/* Add Email if needed and available */}
                             {/* <Grid item xs={12} sm={6}>
                                <DetailItem>
                                     <EmailIcon fontSize="small" color="action" />
                                     <DetailText><strong>Email:</strong> {student.email || 'N/A'}</DetailText>
                                 </DetailItem>
                             </Grid> */}
                        </Grid>
                    </StudentDetailsPaper>
                )}

                {/* Fee Form Section */}
                <SectionTitle variant="h6">
                   <PaymentIcon color="action" /> Payment Details
                </SectionTitle>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Grid container spacing={3}>
                        {/* Amount */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Amount Paid"
                                name="amount"
                                type="number" // Keep type number for input validation
                                value={fee.amount}
                                onChange={handleChange}
                                fullWidth
                                required
                                error={!!formErrors.amount}
                                helperText={formErrors.amount || `Formatted: ₹${formatAmountForDisplay(fee.amount)}`}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CurrencyRupeeIcon color="action" fontSize='small'/>
                                        </InputAdornment>
                                    ),
                                    inputProps: { min: "0.01", step: "0.01" } // Ensure positive amount
                                }}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Payment Date (Display Only) */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Payment Date"
                                name="paymentDateDisplay" // Different name to avoid confusion
                                type="text"
                                value={formatDateForDisplay(fee.paymentDate)} // Display formatted date
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    readOnly: true,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <TodayIcon color="action" fontSize='small' />
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                                helperText="Payment is recorded for today."
                            />
                        </Grid>

                        {/* Payment Mode */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required variant="outlined">
                                <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
                                <Select
                                    labelId="payment-mode-label"
                                    name="paymentMode"
                                    value={fee.paymentMode}
                                    onChange={handleChange}
                                    label="Payment Mode"
                                    startAdornment={ // Add icon to Select
                                        <InputAdornment position="start">
                                            <PaymentsIcon color="action" fontSize='small'/>
                                        </InputAdornment>
                                    }
                                >
                                    <MenuItem value="Cash">Cash</MenuItem>
                                    <MenuItem value="Cheque">Cheque</MenuItem>
                                    <MenuItem value="UPI">UPI</MenuItem>
                                    <MenuItem value="Card">Card (Debit/Credit)</MenuItem>
                                     <MenuItem value="Bank Transfer">Bank Transfer (NEFT/RTGS)</MenuItem>
                                    <MenuItem value="Others">Others</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Reference Number */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Reference / Transaction #"
                                name="referenceNumber"
                                value={fee.referenceNumber}
                                onChange={handleChange}
                                fullWidth
                                error={!!formErrors.referenceNumber}
                                helperText={formErrors.referenceNumber || "E.g., Cheque No, UPI ID, Transaction ID (Optional unless Cheque)"}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <ReceiptLongIcon color="action" fontSize='small'/>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Comments */}
                        <Grid item xs={12}>
                            <TextField
                                label="Comments / Notes"
                                name="comments"
                                multiline
                                rows={2} // Reduced rows
                                value={fee.comments}
                                onChange={handleChange}
                                fullWidth
                                helperText="Optional notes regarding this payment."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CommentIcon color="action" fontSize='small'/>
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end" mt={2}>
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    size="large"
                                    disabled={isSubmitting} // Disable while submitting
                                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <PaymentIcon />}
                                >
                                    {isSubmitting ? 'Processing...' : (feeIdParam ? 'Update Fee Entry' : 'Record Payment')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Confirmation Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={() => !isSubmitting && setOpenDialog(false)} // Prevent closing while submitting
                    maxWidth="xs"
                    fullWidth
                 >
                    <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>Confirm Payment Details</DialogTitle>
                    <DialogContent dividers>
                        <DialogContentText component="div"> {/* Use div for better structure */}
                            <ConfirmationItem>
                                <strong>Amount:</strong> <CurrencyDisplay component="span">₹{formatAmountForDisplay(fee.amount)}</CurrencyDisplay>
                            </ConfirmationItem>
                            <ConfirmationItem>
                                <strong>Payment Date:</strong> {formatDateForDisplay(fee.paymentDate)}
                            </ConfirmationItem>
                            <ConfirmationItem>
                                <strong>Payment Mode:</strong> {fee.paymentMode}
                            </ConfirmationItem>
                            {fee.referenceNumber && (
                                <ConfirmationItem>
                                    <strong>Reference:</strong> {fee.referenceNumber}
                                </ConfirmationItem>
                            )}
                            {fee.comments && (
                                <ConfirmationItem>
                                    <strong>Comments:</strong> {fee.comments}
                                </ConfirmationItem>
                            )}
                            <Alert severity="info" sx={{ mt: 2, fontSize: '0.9rem' }}>
                                Please verify the details above before confirming.
                            </Alert>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                        <Button onClick={() => setOpenDialog(false)} color="inherit" variant='outlined' disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button onClick={confirmSubmit} color="primary" variant="contained" disabled={isSubmitting}>
                            Confirm & Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </StyledPaper>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000} // Slightly shorter duration
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} elevation={6} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </PageContainer>
    );
};

export default FeeFormComponent;
